"use client";

import Image from "next/image";

// Founder + honest-limits section. Isaiah is the face of the brand, so
// this beat carries the portrait and his first-person story. The founder
// intro video was deliberately pulled (founder direction 2026-07-01:
// exactly one video on the page, the product demo in MethodShowcase).
// The honest limits stay because pre-meds reward transparency, and
// naming a real constraint reads more credibly than a roadmap promise.
//
// Copy here is DRAFT pending the pre-launch wording review
// (docs/landing-copy-review.md). The story text mirrors the already-live
// /socials founder note.

interface Limit {
  label: string;
  body: string;
}

const LIMITS: Limit[] = [
  {
    label: "No outcomes data yet.",
    body: "Maren is in open beta. We have not run a long enough cohort to publish score outcomes, and we will not claim any until the data is real.",
  },
  {
    label: "AI can be wrong.",
    body: "Maren is scoped to MCAT prep and built around the conventions of the exam, not on official test content. When it gets something wrong, you can flag it inline and we review every report.",
  },
];

export default function FounderColumn() {
  return (
    <section className="py-20 sm:py-24 bg-white dark:bg-maren-charcoal border-t border-maren-sage/30 dark:border-maren-sage/20">
      <div className="max-w-4xl mx-auto px-6">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-maren-green dark:text-maren-gold text-center">
          Built by a premed, in public
        </p>
        <h2 className="mt-4 font-display font-medium tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.15] text-maren-charcoal dark:text-maren-stone text-center">
          A real student, building the tool he wanted.
        </h2>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-12 gap-8 sm:gap-10 lg:gap-12 items-start">
          <div className="sm:col-span-5">
            <figure>
              <div className="overflow-hidden rounded-2xl border border-maren-sage/40 dark:border-maren-sage/20 shadow-sm">
                <Image
                  src="/marketing/founder-isaiah.jpg"
                  alt="Isaiah, the founder of Maren, at his college graduation in cap and gown."
                  width={1200}
                  height={1500}
                  sizes="(min-width: 640px) 40vw, 100vw"
                  className="w-full h-auto block"
                />
              </div>
              <figcaption className="mt-3 text-sm text-maren-charcoal/65 dark:text-maren-stone/70">
                Isaiah · Founder, premed
              </figcaption>
            </figure>
          </div>

          <div className="sm:col-span-7 space-y-5 text-base sm:text-lg text-maren-charcoal/80 dark:text-maren-stone/85 leading-relaxed">
            <p>
              My name is Isaiah. I went from a 496 diagnostic to a 512 on the
              real MCAT by fixing how I reviewed my mistakes, not by doing more
              questions. 512 was the score my target schools asked for. The
              point was never a perfect score, it was a method that stopped me
              from repeating my mistakes.
            </p>
            <p>
              I built Maren to be the tool I wanted. One place where every
              missed question is logged, tagged, and connected to the next
              thing I should study. I do not build it alone: I bounce ideas
              off top scorers and work with other students and teachers to
              make this the best product possible.
            </p>
            <p>
              The product is still in beta, and I am building it in the open.
              One premed, real students, and a tool that gets better the more
              you use it.
            </p>
          </div>
        </div>

        <div className="mt-12 rounded-2xl border border-maren-sage/40 dark:border-maren-sage/20 bg-maren-stone dark:bg-maren-charcoal-elevated/60 p-6 sm:p-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-maren-green dark:text-maren-gold">
            What Maren is not good at yet
          </p>
          <dl className="mt-5 space-y-5">
            {LIMITS.map(({ label, body }) => (
              <div key={label}>
                <dt className="font-display text-lg sm:text-xl font-medium text-maren-charcoal dark:text-maren-stone leading-snug">
                  {label}
                </dt>
                <dd className="mt-2 text-sm sm:text-[15px] text-maren-charcoal/75 dark:text-maren-stone/80 leading-relaxed">
                  {body}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
}
